import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import type { ApiUser } from '@/services/userApi';
import { updateUserByAdmin } from '@/services/userApi';

interface UserEditDialogProps {
  open: boolean;
  user: ApiUser | null;
  canManageRole: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

type EditForm = {
  name: string;
  email: string;
  role: string;
  team: string;
  active: boolean;
  password: string;
};

const initialForm: EditForm = {
  name: '',
  email: '',
  role: 'User',
  team: '',
  active: true,
  password: '',
};

export default function UserEditDialog({ open, user, canManageRole, onClose, onUpdated }: UserEditDialogProps) {
  const [form, setForm] = useState<EditForm>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!open || !user) {
      return;
    }

    setForm({
      name: user.name,
      email: user.email,
      role: user.role,
      team: user.team ?? '',
      active: user.active,
      password: '',
    });
    setErrorMessage('');
  }, [open, user]);

  const updateField = (field: keyof EditForm, value: string | boolean) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!user) {
      return;
    }

    setSubmitting(true);
    setErrorMessage('');

    try {
      await updateUserByAdmin(user.id, {
        name: form.name.trim(),
        email: form.email.trim(),
        ...(canManageRole ? { role: form.role } : {}),
        team: form.team.trim() || null,
        active: form.active,
        password: form.password.trim() ? form.password : undefined,
      });
      onUpdated();
      onClose();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Nie udało się zapisać zmian użytkownika.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edytuj użytkownika</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

          <TextField
            label="Imię i nazwisko"
            value={form.name}
            onChange={(event) => updateField('name', event.target.value)}
            fullWidth
          />
          <TextField
            label="E-mail"
            value={form.email}
            onChange={(event) => updateField('email', event.target.value)}
            fullWidth
          />
          {canManageRole ? (
            <FormControl fullWidth>
              <InputLabel id="edit-user-role-label">Rola</InputLabel>
              <Select
                labelId="edit-user-role-label"
                label="Rola"
                value={form.role}
                onChange={(event) => updateField('role', event.target.value)}
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Manager">Manager</MenuItem>
                <MenuItem value="User">User</MenuItem>
                <MenuItem value="Viewer">Viewer</MenuItem>
              </Select>
            </FormControl>
          ) : null}
          <TextField
            label="Zespół"
            value={form.team}
            onChange={(event) => updateField('team', event.target.value)}
            fullWidth
          />
          <TextField
            label="Nowe hasło (opcjonalnie)"
            type="password"
            value={form.password}
            onChange={(event) => updateField('password', event.target.value)}
            fullWidth
          />
          <FormControlLabel
            control={<Switch checked={form.active} onChange={(event) => updateField('active', event.target.checked)} />}
            label="Aktywny dostęp"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !form.name.trim() || !form.email.trim()}
        >
          {submitting ? 'Zapisywanie...' : 'Zapisz zmiany'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
